import React, { useState, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const MEAL_COLORS = { breakfast: 'amber', lunch: 'emerald', dinner: 'indigo' };
const MEAL_ICONS = { breakfast: 'Coffee', lunch: 'UtensilsCrossed', dinner: 'Moon' };

export default function TicketValidation({ isAdmin }) {
  const [ticketInput, setTicketInput] = useState('');
  const [validating, setValidating] = useState(false);
  const [result, setResult] = useState(null); // { status: 'success'|'error'|'warning', ticket, message }
  const [recentScans, setRecentScans] = useState([]);
  const [scansLoading, setScansLoading] = useState(false);
  const inputRef = useRef(null);

  const loadRecentScans = async () => {
    setScansLoading(true);
    try {
      const { data } = await supabase
        ?.from('restaurant_scan_logs')
        ?.select('*, restaurant_meal_tickets(ticket_number, meal_type, valid_date, status), restaurant_cardholders(full_name, category)')
        ?.order('scan_time', { ascending: false })
        ?.limit(20);
      setRecentScans(data || []);
    } catch (e) { console.error(e); }
    finally { setScansLoading(false); }
  };

  useState(() => { loadRecentScans(); }, []);

  const validateTicket = async () => {
    const input = ticketInput?.trim();
    if (!input) return;
    setValidating(true);
    setResult(null);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();

      // Find ticket by ticket_number
      const { data: tickets, error: fetchErr } = await supabase
        ?.from('restaurant_meal_tickets')
        ?.select('*, restaurant_cardholders(full_name, category, building, department)')
        ?.eq('ticket_number', input?.toUpperCase())
        ?.limit(1);

      if (fetchErr || !tickets?.length) {
        setResult({ status: 'error', message: 'Ticket not found. Please check the ticket number and try again.' });
        await logScan(null, null, input, 'not_found', user?.id);
        return;
      }

      const ticket = tickets?.[0];

      // Check if expired
      const today = new Date()?.toISOString()?.split('T')?.[0];
      if (ticket?.valid_date < today) {
        setResult({ status: 'error', ticket, message: 'This ticket has expired. It was valid for ' + ticket?.valid_date });
        await logScan(ticket?.id, ticket?.cardholder_id, input, 'expired', user?.id);
        return;
      }

      // Check if already used
      if (ticket?.status === 'used') {
        setResult({ status: 'warning', ticket, message: 'This ticket has already been used. Duplicate scan blocked.' });
        await logScan(ticket?.id, ticket?.cardholder_id, input, 'duplicate', user?.id);
        return;
      }

      // Check if cancelled
      if (ticket?.status === 'cancelled') {
        setResult({ status: 'error', ticket, message: 'This ticket has been cancelled and is no longer valid.' });
        await logScan(ticket?.id, ticket?.cardholder_id, input, 'cancelled', user?.id);
        return;
      }

      // Mark as used
      const { error: updateErr } = await supabase
        ?.from('restaurant_meal_tickets')
        ?.update({ status: 'used', validated_by: user?.id, validated_at: new Date()?.toISOString() })
        ?.eq('id', ticket?.id);

      if (updateErr) {
        setResult({ status: 'error', message: 'Validation failed: ' + updateErr?.message });
        return;
      }

      await logScan(ticket?.id, ticket?.cardholder_id, input, 'validated', user?.id);
      setResult({ status: 'success', ticket: { ...ticket, status: 'used' }, message: 'Ticket validated successfully! Meal access granted.' });
      setTicketInput('');
      loadRecentScans();
    } catch (e) {
      setResult({ status: 'error', message: e?.message || 'Validation error' });
    } finally {
      setValidating(false);
      inputRef?.current?.focus();
    }
  };

  const logScan = async (ticketId, cardholderId, ticketNum, resultStr, userId) => {
    try {
      await supabase?.from('restaurant_scan_logs')?.insert({
        ticket_id: ticketId,
        cardholder_id: cardholderId,
        action: 'scan',
        result: resultStr,
        performed_by: userId,
        notes: `Scanned: ${ticketNum}`,
      });
    } catch (e) { console.error(e); }
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter') validateTicket();
  };

  const resultConfig = {
    success: { bg: 'bg-emerald-50 border-emerald-300', icon: 'CheckCircle2', iconColor: 'text-emerald-500', title: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700' },
    warning: { bg: 'bg-amber-50 border-amber-300', icon: 'AlertTriangle', iconColor: 'text-amber-500', title: 'text-amber-700', badge: 'bg-amber-100 text-amber-700' },
    error: { bg: 'bg-red-50 border-red-300', icon: 'XCircle', iconColor: 'text-red-500', title: 'text-red-700', badge: 'bg-red-100 text-red-700' },
  };

  return (
    <div className="space-y-6">
      {/* Scanner Panel */}
      <div className="bg-card border border-border rounded-xl p-6 space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
            <Icon name="ScanLine" size={20} className="text-amber-600" />
          </div>
          <div>
            <h3 className="font-bold text-foreground">Ticket Validation</h3>
            <p className="text-xs text-muted-foreground">Scan or enter ticket number to validate meal access</p>
          </div>
        </div>

        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Icon name="Hash" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              ref={inputRef}
              type="text"
              value={ticketInput}
              onChange={e => setTicketInput(e?.target?.value?.toUpperCase())}
              onKeyDown={handleKeyDown}
              placeholder="TKT-20260519-XXXXXXXX"
              className="w-full pl-9 pr-4 py-3 border border-border rounded-xl text-sm font-mono bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-amber-300 uppercase"
              autoFocus
            />
          </div>
          <button
            onClick={validateTicket}
            disabled={validating || !ticketInput?.trim()}
            className="px-6 py-3 rounded-xl bg-amber-500 text-white font-bold text-sm flex items-center gap-2 disabled:opacity-50 hover:bg-amber-600 transition-all"
          >
            {validating ? <Icon name="Loader2" size={16} className="animate-spin" /> : <Icon name="ScanLine" size={16} />}
            Validate
          </button>
        </div>

        {/* Result Display */}
        {result && (
          <div className={`border rounded-xl p-4 space-y-3 ${resultConfig?.[result?.status]?.bg}`}>
            <div className="flex items-start gap-3">
              <Icon name={resultConfig?.[result?.status]?.icon} size={22} className={resultConfig?.[result?.status]?.iconColor} />
              <div className="flex-1">
                <p className={`font-bold text-sm ${resultConfig?.[result?.status]?.title}`}>{result?.message}</p>
                {result?.ticket && (
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs text-slate-500 font-medium">Name</div>
                      <div className="text-sm font-bold text-slate-800">{result?.ticket?.restaurant_cardholders?.full_name}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 font-medium">Category</div>
                      <div className="text-sm font-semibold text-slate-700 capitalize">{result?.ticket?.restaurant_cardholders?.category}</div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 font-medium">Meal</div>
                      <div className={`text-sm font-bold capitalize text-${MEAL_COLORS?.[result?.ticket?.meal_type]}-600 flex items-center gap-1`}>
                        <Icon name={MEAL_ICONS?.[result?.ticket?.meal_type] || 'Utensils'} size={13} />
                        {result?.ticket?.meal_type}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 font-medium">Valid Date</div>
                      <div className="text-sm font-semibold text-slate-700">{result?.ticket?.valid_date}</div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-xs text-slate-500 font-medium">Ticket #</div>
                      <div className="text-xs font-mono text-slate-600">{result?.ticket?.ticket_number}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Recent Scans */}
      <div className="bg-card border border-border rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-foreground flex items-center gap-2">
            <Icon name="History" size={16} className="text-muted-foreground" />
            Recent Scan Activity
          </h3>
          <button onClick={loadRecentScans} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1">
            <Icon name="RefreshCw" size={12} />Refresh
          </button>
        </div>

        {scansLoading ? (
          <div className="flex items-center justify-center py-8"><Icon name="Loader2" size={20} className="animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-2">
            {recentScans?.map(scan => {
              const resultColors = {
                validated: 'bg-emerald-100 text-emerald-700',
                duplicate: 'bg-amber-100 text-amber-700',
                not_found: 'bg-red-100 text-red-700',
                expired: 'bg-red-100 text-red-700',
                cancelled: 'bg-slate-100 text-slate-600',
              };
              return (
                <div key={scan?.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-muted/40 hover:bg-muted/70 transition-colors">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${scan?.result === 'validated' ? 'bg-emerald-500' : scan?.result === 'duplicate' ? 'bg-amber-500' : 'bg-red-500'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">
                      {scan?.restaurant_cardholders?.full_name || 'Unknown'}
                    </div>
                    <div className="text-xs text-muted-foreground capitalize">
                      {scan?.restaurant_meal_tickets?.meal_type} · {scan?.restaurant_meal_tickets?.valid_date}
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${resultColors?.[scan?.result] || 'bg-slate-100 text-slate-600'}`}>
                    {scan?.result}
                  </span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {new Date(scan?.scan_time)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              );
            })}
            {recentScans?.length === 0 && (
              <div className="py-8 text-center">
                <Icon name="ScanLine" size={28} className="text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No scan activity yet</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
